import * as React from 'react';
import { EntityCombobox, type EntityLookupItem } from '../ui/EntityCombobox';
import { fetcher } from '../../lib/api-fetcher';

export interface AuctionPickerProps {
  value?: string;
  onChange: (value: string, item?: EntityLookupItem) => void;
  schemeId?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function AuctionPicker({
  value,
  onChange,
  schemeId,
  placeholder = 'Select Auction...',
  disabled,
  className,
}: AuctionPickerProps) {
  const fetchAuctions = React.useCallback(
    async (query: string): Promise<EntityLookupItem[]> => {
      try {
        const params = new URLSearchParams();
        params.set('limit', '30');
        if (query) params.set('q', query);
        const url = `/staff/v1/auctions?${params.toString()}`;
        const res = await fetcher.get<{
          data?: Array<{
            id: string;
            schemeId?: string;
            schemeName?: string | null;
            cycleNumber?: number;
            auctionType?: string;
            status?: string;
            scheduledDate?: string | null;
            netPrizeAmount?: number | null;
          }>;
        }>(url);
        let list = res?.data || [];
        if (schemeId) {
          list = list.filter((a) => a.schemeId === schemeId);
        }
        return list.map((a) => ({
          id: a.id,
          code: `Auction #${a.cycleNumber ?? '—'}`,
          title: `Cycle ${a.cycleNumber ?? '—'}${a.schemeName ? ` (${a.schemeName})` : ''}`,
          subtitle: a.scheduledDate ? `Date: ${a.scheduledDate}` : undefined,
          badge: a.status,
        }));
        // allow-silent-catch: return empty list on search failure
      } catch {
        return [];
      }
    },
    [schemeId]
  );

  return (
    <EntityCombobox
      value={value}
      onChange={onChange}
      fetchItems={fetchAuctions}
      placeholder={placeholder}
      searchPlaceholder="Search auction by cycle number..."
      disabled={disabled}
      className={className}
    />
  );
}
